import * as React from "react";
import {
  Button,
  Dialog,
  DialogSurface,
  Input,
  Radio,
  RadioGroup,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";

type YesNo = "ja" | "nein" | "";

type YesNoItem = {
  answer: YesNo;
  description: string;
};

type ToggleAmount = {
  has: YesNo;
  amount: string;
};

export type BusinessAssetsRiskData = {
  questions: Record<string, YesNoItem>;
  safeguards: Record<string, YesNoItem>;
  equipment: {
    commercial: ToggleAmount;
    technical: {
      has: YesNo;
      electronics: ToggleAmount;
      machines: ToggleAmount;
      software: ToggleAmount;
    };
    vehicles: {
      has: YesNo;
      fixedInstallations: ToggleAmount;
      mobile: ToggleAmount;
    };
  };
  forklifts: {
    hasMachines: YesNo;
    count: string;
    maxSpeedUpTo20: string;
    maxSpeedOver20: string;
    totalSum: string;
  };
  inventory: {
    hasGoods: YesNo;
    semifinishedExists: YesNo;
    semifinishedAvg: string;
    semifinishedMax: string;
    finishedExists: YesNo;
    finishedStockPrevYear: string;
    finishedLowest: string;
    finishedHighest: string;
    finishedAverage: string;
    perishable: YesNo;
    perishableDescription: string;
    otherExists: YesNo;
    other: string;
  };
  otherNotes: string;
  requiredSum: string;
};

type QuestionDef = { id: string; label: string; placeholder?: string };

const QUESTION_DEFS: QuestionDef[] = [
  {
    id: "leasedPremises",
    label: "Ist der Betrieb in gepachteten / gemieteten / geleasten Räumlichkeiten / Gebäuden?",
  },
  {
    id: "tenantRisk",
    label: "Tragen Sie das Risiko für die betrieblich genutzten Räumlichkeiten / Gebäude?",
  },
  { id: "subletting", label: "Erfolgt eine Untervermietung?" },
  { id: "addedBuildingParts", label: "Wurden von Ihnen Gebäudebestandteile eingebracht?" },
  {
    id: "valuables",
    label:
      "Sind Bargeld, Urkunden, Münzen, Medaillen, Edelmetalle, Schmucksachen etc. vorhanden?",
  },
  { id: "softwareDataLoss", label: "Sind Softwarelizenzen und Datenverluste abzusichern?" },
  {
    id: "commercialTechnicalEquipment",
    label: "Ist technische / kaufmännische Betriebseinrichtung vorhanden?",
  },
  {
    id: "equipmentInCustody",
    label:
      "Ist betriebs- und/oder technische Einrichtung gemietet, geleast, finanziert, zu sonstigen Zwecken in Obhut? / Sind Sie zum Versicherungsschutz verpflichtet?",
  },
  { id: "seasonalFluctuations", label: "Gibt es saisonale Schwankungen?" },
  {
    id: "goodsTransport",
    label: "Wenn Waren vorhanden, wie werden diese transportiert, bezogen und versendet?",
  },
  {
    id: "additionalGoods",
    label:
      "Wenn Waren vorhanden, gibt es noch andere Waren / Vorräte, die Sie vertreiben (z. B. branchenunabhängig oder ergänzend)? Gibt es Schwankungen?",
  },
  { id: "artworks", label: "Gibt es Kunstwerke in Ihren Räumlichkeiten / in der Firma?" },
  { id: "assemblyObjects", label: "Sind eigene oder fremde Montageobjekte in Planung oder in Ausführung?" },
];

const GOODS_DEPENDENT_IDS = new Set(["goodsTransport", "additionalGoods"]);

const SAFEGUARD_DEFS: QuestionDef[] = [
  { id: "alarmSystem", label: "Einbruchmeldeanlage u. Echtzeitüberwachung" },
  { id: "fireAlarm", label: "Brandmeldeanlage" },
  { id: "smokeHeat", label: "Rauch- u. Wärmeabzugsanlagen" },
  { id: "lightningProtection", label: "Blitzschutz" },
  { id: "fireProtectionConcept", label: "Brandschutzkonzept" },
  { id: "sprinkler", label: "Sprinkleranlage" },
  { id: "videoSurveillance", label: "Videoüberwachung" },
  { id: "propertyFencing", label: "Einfriedung des Grundstücks" },
  { id: "otherSafeguards", label: "Sonstiges" },
];

const useStyles = makeStyles({
  surface: { width: "min(1100px, 94vw)", maxWidth: "1100px" },
  body: { padding: "0", display: "block", overflow: "auto", maxHeight: "calc(90vh - 88px)" },
  navBar: {
    padding: "10px 24px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 5,
  },
  title: { fontWeight: 600, margin: 0 },
  content: { padding: "16px 24px", display: "grid", gap: "16px" },
  card: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "16px",
    display: "grid",
    gap: "12px",
    background: tokens.colorNeutralBackground1,
  },
  sectionTitle: { fontWeight: 600, marginTop: "4px" },
  field: { display: "grid", gap: "6px" },
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    "@media (max-width: 700px)": { gridTemplateColumns: "1fr" },
  },
  row3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "12px",
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr 1fr" },
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  },
  row4: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    "@media (max-width: 1000px)": { gridTemplateColumns: "repeat(2, 1fr)" },
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  },
  questionRow: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 2fr) auto minmax(220px, 1fr)",
    gap: "12px",
    alignItems: "center",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
      alignItems: "start",
    },
  },
  actions: {
    padding: "16px 24px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex",
    justifyContent: "space-between",
  },
});

const buildYesNoMap = (defs: QuestionDef[]) =>
  defs.reduce<Record<string, YesNoItem>>((acc, def) => {
    acc[def.id] = { answer: "", description: "" };
    return acc;
  }, {});

const createEmptyData = (): BusinessAssetsRiskData => ({
  questions: buildYesNoMap(QUESTION_DEFS),
  safeguards: buildYesNoMap(SAFEGUARD_DEFS),
  equipment: {
    commercial: { has: "", amount: "" },
    technical: {
      has: "",
      electronics: { has: "", amount: "" },
      machines: { has: "", amount: "" },
      software: { has: "", amount: "" },
    },
    vehicles: {
      has: "",
      fixedInstallations: { has: "", amount: "" },
      mobile: { has: "", amount: "" },
    },
  },
  forklifts: {
    hasMachines: "",
    count: "",
    maxSpeedUpTo20: "",
    maxSpeedOver20: "",
    totalSum: "",
  },
  inventory: {
    hasGoods: "",
    semifinishedExists: "",
    semifinishedAvg: "",
    semifinishedMax: "",
    finishedExists: "",
    finishedStockPrevYear: "",
    finishedLowest: "",
    finishedHighest: "",
    finishedAverage: "",
    perishable: "",
    perishableDescription: "",
    otherExists: "",
    other: "",
  },
  otherNotes: "",
  requiredSum: "",
});

type Props = {
  open: boolean;
  onClose: () => void;
  value?: BusinessAssetsRiskData | null;
  onSave: (next: BusinessAssetsRiskData) => void;
  title?: string;
  inline?: boolean;
};

export default function BusinessAssetsDialog({
  open,
  onClose,
  value,
  onSave,
  title = "Risikoobjekt: Betriebsvermögen",
  inline = false,
}: Props) {
  const s = useStyles();
  const [draft, setDraft] = React.useState<BusinessAssetsRiskData>(value ?? createEmptyData());
  const goodsPresent = draft.inventory.hasGoods === "ja";
  const leasedPremises = draft.questions.leasedPremises?.answer === "ja";

  React.useEffect(() => {
    if (!open && !inline) return;
    setDraft(value ?? createEmptyData());
  }, [open, value, inline]);

  const updateQuestion = (id: string, patch: Partial<YesNoItem>) => {
    setDraft((prev) => ({
      ...prev,
      questions: { ...prev.questions, [id]: { ...prev.questions[id], ...patch } },
    }));
  };

  const updateSafeguard = (id: string, patch: Partial<YesNoItem>) => {
    setDraft((prev) => ({
      ...prev,
      safeguards: { ...prev.safeguards, [id]: { ...prev.safeguards[id], ...patch } },
    }));
  };

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  const editor = (
    <>
        <div className={s.navBar}>
          <h2 className={s.title}>{title}</h2>
          {!inline && (
            <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} aria-label="Schließen" />
          )}
        </div>
        <div className={s.body}>
          <div className={s.content}>
            <div className={s.card}>
              <div className={s.sectionTitle}>Betriebsvermögen</div>
              {QUESTION_DEFS.map((q) => {
                if ((q.id === "tenantRisk" || q.id === "subletting" || q.id === "addedBuildingParts")  && !leasedPremises) return null;
                if (GOODS_DEPENDENT_IDS.has(q.id) && !goodsPresent) return null;
                const item = draft.questions[q.id];
                if (!item) return null;
                return (
                  <div className={s.questionRow} key={q.id}>
                    <div>{q.label}</div>
                    <RadioGroup
                      layout="horizontal"
                      value={item.answer}
                      onChange={(_, d) =>
                        updateQuestion(q.id, { answer: (d.value as YesNo) ?? "" })
                      }
                    >
                      <Radio value="nein" label="nein" />
                      <Radio value="ja" label="ja" />
                    </RadioGroup>
                    {item.answer === "ja" && (
                      <Input
                        value={item.description}
                        onChange={(_, d) => updateQuestion(q.id, { description: d.value })}
                        placeholder="Kurzbeschreibung"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className={s.card}>
              <div className={s.sectionTitle}>Vorhandene Sicherungen</div>
              {SAFEGUARD_DEFS.map((q) => {
                const item = draft.safeguards[q.id];
                return (
                  <div className={s.questionRow} key={q.id}>
                    <div>{q.label}</div>
                    <RadioGroup
                      layout="horizontal"
                      value={item.answer}
                      onChange={(_, d) =>
                        updateSafeguard(q.id, { answer: (d.value as YesNo) ?? "" })
                      }
                    >
                      <Radio value="nein" label="nein" />
                      <Radio value="ja" label="ja" />
                    </RadioGroup>
                    {item.answer === "ja" && (
                      <Input
                        value={item.description}
                        onChange={(_, d) => updateSafeguard(q.id, { description: d.value })}
                        placeholder="Kurzbeschreibung"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className={s.card}>
              <div className={s.sectionTitle}>Betriebseinrichtungen zum Neuwert</div>
              <div className={s.row2}>
                <div className={s.field}>
                  <label>kaufmännische</label>
                  <RadioGroup
                    layout="horizontal"
                    value={draft.equipment.commercial.has}
                    onChange={(_, d) =>
                      setDraft((prev) => ({
                        ...prev,
                        equipment: {
                          ...prev.equipment,
                          commercial: { ...prev.equipment.commercial, has: (d.value as YesNo) ?? "" },
                        },
                      }))
                    }
                  >
                    <Radio value="nein" label="nein" />
                    <Radio value="ja" label="ja" />
                  </RadioGroup>
                </div>
                {draft.equipment.commercial.has === "ja" && (
                  <div className={s.field}>
                    <label>Summe</label>
                    <Input
                      value={draft.equipment.commercial.amount}
                      onChange={(_, d) =>
                        setDraft((prev) => ({
                          ...prev,
                          equipment: {
                            ...prev.equipment,
                            commercial: { ...prev.equipment.commercial, amount: d.value },
                          },
                        }))
                      }
                      placeholder=""
                    />
                  </div>
                )}
              </div>

              <div className={s.row2}>
                <div className={s.field}>
                  <label>technische</label>
                  <RadioGroup
                    layout="horizontal"
                    value={draft.equipment.technical.has}
                    onChange={(_, d) =>
                      setDraft((prev) => ({
                        ...prev,
                        equipment: {
                          ...prev.equipment,
                          technical: { ...prev.equipment.technical, has: (d.value as YesNo) ?? "" },
                        },
                      }))
                    }
                  >
                    <Radio value="nein" label="nein" />
                    <Radio value="ja" label="ja" />
                  </RadioGroup>
                </div>
              </div>
              {draft.equipment.technical.has === "ja" && (
                <>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>Elektronik</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.equipment.technical.electronics.has}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            equipment: {
                              ...prev.equipment,
                              technical: {
                                ...prev.equipment.technical,
                                electronics: {
                                  ...prev.equipment.technical.electronics,
                                  has: (d.value as YesNo) ?? "",
                                },
                              },
                            },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                    {draft.equipment.technical.electronics.has === "ja" && (
                      <div className={s.field}>
                        <label>Summe</label>
                        <Input
                          value={draft.equipment.technical.electronics.amount}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              equipment: {
                                ...prev.equipment,
                                technical: {
                                  ...prev.equipment.technical,
                                  electronics: {
                                    ...prev.equipment.technical.electronics,
                                    amount: d.value,
                                  },
                                },
                              },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                    )}
                  </div>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>Maschinen</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.equipment.technical.machines.has}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            equipment: {
                              ...prev.equipment,
                              technical: {
                                ...prev.equipment.technical,
                                machines: {
                                  ...prev.equipment.technical.machines,
                                  has: (d.value as YesNo) ?? "",
                                },
                              },
                            },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                    {draft.equipment.technical.machines.has === "ja" && (
                      <div className={s.field}>
                        <label>Summe</label>
                        <Input
                          value={draft.equipment.technical.machines.amount}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              equipment: {
                                ...prev.equipment,
                                technical: {
                                  ...prev.equipment.technical,
                                  machines: {
                                    ...prev.equipment.technical.machines,
                                    amount: d.value,
                                  },
                                },
                              },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                    )}
                  </div>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>Software</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.equipment.technical.software.has}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            equipment: {
                              ...prev.equipment,
                              technical: {
                                ...prev.equipment.technical,
                                software: {
                                  ...prev.equipment.technical.software,
                                  has: (d.value as YesNo) ?? "",
                                },
                              },
                            },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                    {draft.equipment.technical.software.has === "ja" && (
                      <div className={s.field}>
                        <label>Summe</label>
                        <Input
                          value={draft.equipment.technical.software.amount}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              equipment: {
                                ...prev.equipment,
                                technical: {
                                  ...prev.equipment.technical,
                                  software: {
                                    ...prev.equipment.technical.software,
                                    amount: d.value,
                                  },
                                },
                              },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className={s.row2}>
                <div className={s.field}>
                  <label>in Fahrzeugen (mobile Geräte oder fest verbaute Technik)</label>
                  <RadioGroup
                    layout="horizontal"
                    value={draft.equipment.vehicles.has}
                    onChange={(_, d) =>
                      setDraft((prev) => ({
                        ...prev,
                        equipment: {
                          ...prev.equipment,
                          vehicles: { ...prev.equipment.vehicles, has: (d.value as YesNo) ?? "" },
                        },
                      }))
                    }
                  >
                    <Radio value="nein" label="nein" />
                    <Radio value="ja" label="ja" />
                  </RadioGroup>
                </div>
              </div>
              {draft.equipment.vehicles.has === "ja" && (
                <>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>fest - z. B. Einbauten</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.equipment.vehicles.fixedInstallations.has}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            equipment: {
                              ...prev.equipment,
                              vehicles: {
                                ...prev.equipment.vehicles,
                                fixedInstallations: {
                                  ...prev.equipment.vehicles.fixedInstallations,
                                  has: (d.value as YesNo) ?? "",
                                },
                              },
                            },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                    {draft.equipment.vehicles.fixedInstallations.has === "ja" && (
                      <div className={s.field}>
                        <label>Summe</label>
                        <Input
                          value={draft.equipment.vehicles.fixedInstallations.amount}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              equipment: {
                                ...prev.equipment,
                                vehicles: {
                                  ...prev.equipment.vehicles,
                                  fixedInstallations: {
                                    ...prev.equipment.vehicles.fixedInstallations,
                                    amount: d.value,
                                  },
                                },
                              },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                    )}
                  </div>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>mobil - z. B. Werkzeuge, Materialien, Waren, etc.</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.equipment.vehicles.mobile.has}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            equipment: {
                              ...prev.equipment,
                              vehicles: {
                                ...prev.equipment.vehicles,
                                mobile: {
                                  ...prev.equipment.vehicles.mobile,
                                  has: (d.value as YesNo) ?? "",
                                },
                              },
                            },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                    {draft.equipment.vehicles.mobile.has === "ja" && (
                      <div className={s.field}>
                        <label>Summe</label>
                        <Input
                          value={draft.equipment.vehicles.mobile.amount}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              equipment: {
                                ...prev.equipment,
                                vehicles: {
                                  ...prev.equipment.vehicles,
                                  mobile: {
                                    ...prev.equipment.vehicles.mobile,
                                    amount: d.value,
                                  },
                                },
                              },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className={s.card}>
              <div className={s.sectionTitle}>Arbeitsmaschinen / Gabelstapler (mobil)</div>
              <div className={s.row2}>
                <div className={s.field}>
                  <label>Haben Sie Arbeitsmaschinen?</label>
                  <RadioGroup
                    layout="horizontal"
                    value={draft.forklifts.hasMachines}
                    onChange={(_, d) =>
                      setDraft((prev) => ({
                        ...prev,
                        forklifts: { ...prev.forklifts, hasMachines: (d.value as YesNo) ?? "" },
                      }))
                    }
                  >
                    <Radio value="nein" label="nein" />
                    <Radio value="ja" label="ja" />
                  </RadioGroup>
                </div>
              </div>
              {draft.forklifts.hasMachines === "ja" && (
                <>
                  <div className={s.row3}>
                    <div className={s.field}>
                      <label>Anzahl Gabelstapler</label>
                      <Input
                        value={draft.forklifts.count}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            forklifts: { ...prev.forklifts, count: d.value },
                          }))
                        }
                        placeholder="Anzahl"
                      />
                    </div>
                    <div className={s.field}>
                      <label>Höchstgeschwindigkeit bis 20 km/h (Anzahl)</label>
                      <Input
                        value={draft.forklifts.maxSpeedUpTo20}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            forklifts: { ...prev.forklifts, maxSpeedUpTo20: d.value },
                          }))
                        }
                        placeholder=""
                      />
                    </div>
                    <div className={s.field}>
                      <label>Höchstgeschwindigkeit über 20 km/h (Anzahl)</label>
                      <Input
                        value={draft.forklifts.maxSpeedOver20}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            forklifts: { ...prev.forklifts, maxSpeedOver20: d.value },
                          }))
                        }
                        placeholder=""
                      />
                    </div>
                  </div>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>Summe Gesamt</label>
                      <Input
                        value={draft.forklifts.totalSum}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            forklifts: { ...prev.forklifts, totalSum: d.value },
                          }))
                        }
                        placeholder=""
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={s.card}>
              <div className={s.sectionTitle}>Waren / Vorräte</div>
              <div className={s.row2}>
                <div className={s.field}>
                  <label>Sind Waren/Vorräte vorhanden?</label>
                  <RadioGroup
                    layout="horizontal"
                    value={draft.inventory.hasGoods}
                    onChange={(_, d) =>
                      setDraft((prev) => ({
                        ...prev,
                        inventory: { ...prev.inventory, hasGoods: (d.value as YesNo) ?? "" },
                      }))
                    }
                  >
                    <Radio value="nein" label="nein" />
                    <Radio value="ja" label="ja" />
                  </RadioGroup>
                </div>
              </div>
              {draft.inventory.hasGoods === "ja" && (
                <>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>halbfertige (wie Rohstoffe, Hilfsmittel)</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.inventory.semifinishedExists}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            inventory: {
                              ...prev.inventory,
                              semifinishedExists: (d.value as YesNo) ?? "",
                            },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                  </div>
                  {draft.inventory.semifinishedExists === "ja" && (
                    <div className={s.row2}>
                      <div className={s.field}>
                        <label>Durchschnittssumme</label>
                        <Input
                          value={draft.inventory.semifinishedAvg}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              inventory: { ...prev.inventory, semifinishedAvg: d.value },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                      <div className={s.field}>
                        <label>Höchstsumme</label>
                        <Input
                          value={draft.inventory.semifinishedMax}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              inventory: { ...prev.inventory, semifinishedMax: d.value },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                    </div>
                  )}
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>fertige Waren</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.inventory.finishedExists}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            inventory: {
                              ...prev.inventory,
                              finishedExists: (d.value as YesNo) ?? "",
                            },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                  </div>
                  {draft.inventory.finishedExists === "ja" && (
                    <>
                      <div className={s.row2}>
                        <div className={s.field}>
                          <label>
                            Warenbestand zum 31.12. des vorherigen Jahres (oder zum Ende des Geschäftsjahres)
                          </label>
                          <Input
                            value={draft.inventory.finishedStockPrevYear}
                            onChange={(_, d) =>
                              setDraft((prev) => ({
                                ...prev,
                                inventory: { ...prev.inventory, finishedStockPrevYear: d.value },
                              }))
                            }
                            placeholder=""
                          />
                        </div>
                        <div className={s.field}>
                          <label>Niedrigster Bestand</label>
                          <Input
                            value={draft.inventory.finishedLowest}
                            onChange={(_, d) =>
                              setDraft((prev) => ({
                                ...prev,
                                inventory: { ...prev.inventory, finishedLowest: d.value },
                              }))
                            }
                            placeholder=""
                          />
                        </div>
                      </div>
                      <div className={s.row2}>
                        <div className={s.field}>
                          <label>Höchstbestand</label>
                          <Input
                            value={draft.inventory.finishedHighest}
                            onChange={(_, d) =>
                              setDraft((prev) => ({
                                ...prev,
                                inventory: { ...prev.inventory, finishedHighest: d.value },
                              }))
                            }
                            placeholder=""
                          />
                        </div>
                        <div className={s.field}>
                          <label>Durchschnittsbestand</label>
                          <Input
                            value={draft.inventory.finishedAverage}
                            onChange={(_, d) =>
                              setDraft((prev) => ({
                                ...prev,
                                inventory: { ...prev.inventory, finishedAverage: d.value },
                              }))
                            }
                            placeholder=""
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>Gibt es verderbliche Waren?</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.inventory.perishable}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            inventory: { ...prev.inventory, perishable: (d.value as YesNo) ?? "" },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                    {draft.inventory.perishable === "ja" && (
                      <div className={s.field}>
                        <label>Kurzbeschreibung</label>
                        <Input
                          value={draft.inventory.perishableDescription}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              inventory: { ...prev.inventory, perishableDescription: d.value },
                            }))
                          }
                          placeholder="Kurzbeschreibung"
                        />
                      </div>
                    )}
                  </div>
                  <div className={s.row2}>
                    <div className={s.field}>
                      <label>Sonstiges</label>
                      <RadioGroup
                        layout="horizontal"
                        value={draft.inventory.otherExists}
                        onChange={(_, d) =>
                          setDraft((prev) => ({
                            ...prev,
                            inventory: { ...prev.inventory, otherExists: (d.value as YesNo) ?? "" },
                          }))
                        }
                      >
                        <Radio value="nein" label="nein" />
                        <Radio value="ja" label="ja" />
                      </RadioGroup>
                    </div>
                  </div>
                  {draft.inventory.otherExists === "ja" && (
                    <div className={s.row2}>
                      <div className={s.field}>
                        <label>Sonstiges</label>
                        <Input
                          value={draft.inventory.other}
                          onChange={(_, d) =>
                            setDraft((prev) => ({
                              ...prev,
                              inventory: { ...prev.inventory, other: d.value },
                            }))
                          }
                          placeholder=""
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={s.card}>
              <div className={s.row2}>
                <div className={s.field}>
                  <label>Sonstiges</label>
                  <Textarea
                    value={draft.otherNotes}
                    onChange={(_, d) => setDraft((prev) => ({ ...prev, otherNotes: d.value }))}
                    placeholder="..."
                  />
                </div>
                <div className={s.field}>
                  <label>benötigte Versicherungssumme</label>
                  <Input
                    value={draft.requiredSum}
                    onChange={(_, d) => setDraft((prev) => ({ ...prev, requiredSum: d.value }))}
                    placeholder=""
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={s.actions}>
          <Button onClick={onClose}>{inline ? "Zurück" : "Abbrechen"}</Button>
          <Button appearance="primary" onClick={handleSave}>
            Speichern
          </Button>
        </div>
    </>
  );

  if (inline) {
    return <div>{editor}</div>;
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={s.surface} aria-describedby={undefined}>
        {editor}
      </DialogSurface>
    </Dialog>
  );
}
